import React from 'react'
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CelebrationIcon from '@mui/icons-material/Celebration'
import moment from 'moment'
import { WidgetFrame, WidgetUnavailable } from './WidgetFrame'

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

// '#RRGGBB' + alpha suffix — the viz palette is plain hex in both modes
const tint = (hex, alpha = '26') => `${hex}${alpha}`

export const ShoutoutsWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const shoutouts = data?.shoutouts
  return (
    <WidgetFrame title="Shoutouts" accent={viz.shoutout}>
      {!shoutouts || shoutouts.error ? (
        <WidgetUnavailable />
      ) : shoutouts.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <CelebrationIcon sx={{ fontSize: 42, color: 'text.secondary' }} />
          <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
            No shoutouts yet — send one from your profile menu
          </Typography>
        </Box>
      ) : (
        <Stack gap={1.25} sx={{ overflow: 'hidden' }}>
          {shoutouts.map((shoutout) => (
            <Stack key={shoutout._id} direction="row" gap={1.5} alignItems="flex-start">
              <Avatar
                sx={{
                  bgcolor: tint(viz.shoutout),
                  color: viz.shoutout,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {initials(shoutout.to) || '?'}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {shoutout.to ?? 'A colleague'}
                  <Typography
                    component="span"
                    sx={{ color: 'text.secondary', fontWeight: 400, ml: 1, fontSize: '0.85rem' }}
                  >
                    from {shoutout.from ?? 'a colleague'} ·{' '}
                    {moment(shoutout.createdAt).fromNow()}
                  </Typography>
                </Typography>
                <Typography sx={{ color: 'text.primary', fontSize: '1rem' }}>
                  “{shoutout.shoutOut}”
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </WidgetFrame>
  )
}

export const OnDutyWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const onDuty = data?.onDutyNow
  return (
    <WidgetFrame
      title="On duty now"
      accent={viz.good}
      action={
        onDuty && !onDuty.error ? (
          <Chip
            size="small"
            label={onDuty.count}
            sx={{ fontWeight: 700, bgcolor: tint(viz.good), color: viz.good }}
          />
        ) : null
      }
    >
      {!onDuty || onDuty.error ? (
        <WidgetUnavailable />
      ) : onDuty.crew.length === 0 ? (
        <WidgetUnavailable label="Nobody is on shift right now" />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 1,
            overflow: 'hidden',
            alignContent: 'start',
          }}
        >
          {onDuty.crew.map((member, index) => (
            <Stack key={index} direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: tint(viz.good),
                  color: viz.good,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {initials(member.name) || '?'}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {member.name}
                </Typography>
                {member.role && (
                  <Typography noWrap sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {member.role}
                  </Typography>
                )}
              </Box>
            </Stack>
          ))}
        </Box>
      )}
    </WidgetFrame>
  )
}

export const UpcomingEventsWidget = ({ data }) => {
  const { viz } = useTheme().palette
  const events = data?.upcomingEvents
  return (
    <WidgetFrame title="Upcoming events" accent={viz.series}>
      {!events || events.error ? (
        <WidgetUnavailable />
      ) : events.length === 0 ? (
        <WidgetUnavailable label="No events in the next 30 days" />
      ) : (
        <Stack gap={1.25} sx={{ overflow: 'hidden' }}>
          {events.map((event) => {
            const filled = `${event.shiftsFilled}/${event.shiftsTotal}`
            const fullyCrewed =
              event.shiftsTotal > 0 && event.shiftsFilled >= event.shiftsTotal
            return (
              <Stack
                key={event._id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 600 }}>
                    {event.title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    {moment(event.startDate).format('ddd D MMM')}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${filled} crewed`}
                  sx={{
                    fontWeight: 700,
                    flexShrink: 0,
                    bgcolor: fullyCrewed ? tint(viz.good) : tint(viz.warning),
                    color: fullyCrewed ? viz.good : viz.warning,
                  }}
                />
              </Stack>
            )
          })}
        </Stack>
      )}
    </WidgetFrame>
  )
}
